import {
  ArrowsUpDownIcon,
  CheckIcon,
  ChevronDoubleDownIcon,
  ChevronDoubleUpIcon,
  ChevronDownIcon,
  ClipboardDocumentIcon,
  EllipsisVerticalIcon,
  PencilIcon,
  PlusIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import axios from "axios";
import cn from "classnames";
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  Fragment,
} from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { Virtuoso } from "react-virtuoso";
import { AudioMetadataType } from "../../db/schema";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { isWhisperFormat, isTranscribeFormat } from "../../utils/transcriptionHelpers";
import Button from "../common/Button";
import Input from "../common/Input";
import FindBar from "./FindBar";

// Type for case files from database
interface CaseFileData {
  id: string;
  metadata?: {
    speakerMap?: Record<string, { name: string; initials: string }>;
    [key: string]: any;
  } | null;
  [key: string]: any;
}

interface TranscriptionProps {
  transcription?:
    | {
        task: string;
        language: string;
        duration: number;
        text: string;
        words: {
          word: string;
          start: number;
          end: number;
        }[];
      }
    | {
        jobName: string;
        accountId: string;
        status: string;
        results: {
          transcripts: {
            transcript: string;
          }[];
          speaker_labels: {
            segments: Array<{
              start_time: string;
              end_time: string;
              speaker_label: string;
              items: Array<{
                speaker_label: string;
                start_time: string;
                end_time: string;
              }>;
            }>;
            channel_label: string;
            speakers: number;
          };
          items: Array<{
            id: number;
            type: string;
            alternatives: Array<{
              confidence: string;
              content: string;
            }>;
            start_time?: string;
            end_time?: string;
            speaker_label?: string;
          }>;
          audio_segments: Array<{
            id: number;
            transcript: string;
            start_time: string;
            end_time: string;
            speaker_label: string;
            items: number[];
          }>;
        };
      }
    | null;
  onWordClick?: (time: number, expand?: boolean) => void;
  currentTime?: number;
  caseAudioMetadata?: AudioMetadataType | null;
  setCaseAudioMetadata?: React.Dispatch<
    React.SetStateAction<AudioMetadataType | null>
  >;
  isFollowing?: boolean;
  setIsFollowing?: React.Dispatch<React.SetStateAction<boolean>>;
  caseId?: string;
  fileId?: string;
  onRegenerateStart?: () => void;
  onRegenerateEnd?: () => void;
  handleSelectedCase: (caseId: string) => void;
  isReadOnly?: boolean;
  setCaseTranscription?: React.Dispatch<React.SetStateAction<any>>;
  caseFiles?: CaseFileData[];
  /** Ignore Cmd/Ctrl+F while the transcript is mounted but hidden (footage view) */
  findDisabled?: boolean;
  /** Where the find bar renders: the slot under the editor toolbar, above the tabs */
  findPortal?: HTMLElement | null;
  /** Find bar open state, owned by Editor so the toolbar button can toggle it */
  findOpen?: boolean;
  setFindOpen?: (open: boolean) => void;
}

type FindRange = { start: number; end: number; current: boolean };

// Wrap the parts of `text` covered by find matches in the editor's
// search-result highlight. `offset` is where `text` starts within the
// segment's full text (whisper renders a segment as one span per word).
const highlightFindMatches = (
  text: string,
  offset: number,
  ranges?: FindRange[]
): React.ReactNode => {
  if (!ranges?.length) return text;
  const parts: React.ReactNode[] = [];
  let cursor = 0;
  for (const range of ranges) {
    const start = Math.max(range.start - offset, cursor);
    const end = Math.min(range.end - offset, text.length);
    if (end <= start) continue;
    if (start > cursor) parts.push(text.slice(cursor, start));
    parts.push(
      <span
        key={start}
        className={
          range.current ? "search-result search-result-current" : "search-result"
        }
        data-find-current={range.current || undefined}
      >
        {text.slice(start, end)}
      </span>
    );
    cursor = end;
  }
  if (!parts.length) return text;
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
};

const Transcription = memo(function Transcription({
  transcription = null,
  onWordClick,
  currentTime = 0,
  caseAudioMetadata,
  setCaseAudioMetadata,
  isFollowing = false,
  setIsFollowing,
  caseId,
  fileId,
  onRegenerateStart,
  onRegenerateEnd,
  handleSelectedCase,
  isReadOnly = false,
  setCaseTranscription,
  caseFiles = [],
  findDisabled = false,
  findPortal,
  findOpen: showFind = false,
  setFindOpen,
}: TranscriptionProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const virtuosoRef = useRef<any>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isAutoScrollingRef = useRef(false);
  const userClickedSegmentRef = useRef(false);
  const [isRegenerating, setIsRegenerating] = useState(false);
  const confirmDialogRef = useRef<HTMLDialogElement>(null);
  const speakerEditDialogRef = useRef<HTMLDialogElement>(null);
  const addSpeakerDialogRef = useRef<HTMLDialogElement>(null);
  const deleteSpeakerDialogRef = useRef<HTMLDialogElement>(null);
  const [editingSegmentId, setEditingSegmentId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState<string>("");
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [savingSpeakerSegmentId, setSavingSpeakerSegmentId] = useState<
    number | null
  >(null);
  const [mergingUpSegmentId, setMergingUpSegmentId] = useState<number | null>(
    null
  );
  const [mergingDownSegmentId, setMergingDownSegmentId] = useState<
    number | null
  >(null);
  const [splittingSegmentId, setSplittingSegmentId] = useState<number | null>(
    null
  );
  const [showSplitInterface, setShowSplitInterface] = useState<number | null>(
    null
  );
  const [deletingSegmentId, setDeletingSegmentId] = useState<number | null>(
    null
  );
  const [editingTimestampId, setEditingTimestampId] = useState<number | null>(
    null
  );
  const [editingTimestamp, setEditingTimestamp] = useState<string>("");
  const [isSavingTimestamp, setIsSavingTimestamp] = useState(false);
  const [editingSpeakerNameId, setEditingSpeakerNameId] = useState<
    string | null
  >(null);
  const [editingSpeakerName, setEditingSpeakerName] = useState<string>("");
  const [editingSpeakerInitials, setEditingSpeakerInitials] =
    useState<string>("");
  const [isSavingSpeakerName, setIsSavingSpeakerName] = useState(false);
  const [addingSpeakerSegmentId, setAddingSpeakerSegmentId] = useState<
    number | null
  >(null);
  const [newSpeakerName, setNewSpeakerName] = useState<string>("");
  const [newSpeakerInitials, setNewSpeakerInitials] = useState<string>("");
  const [isSavingNewSpeaker, setIsSavingNewSpeaker] = useState(false);
  const [deletingSpeakerLabel, setDeletingSpeakerLabel] = useState<
    string | null
  >(null);
  const [replacementSpeakerLabel, setReplacementSpeakerLabel] =
    useState<string>("");
  const [isDeletingSpeaker, setIsDeletingSpeaker] = useState(false);

  // Throttle currentTime to reduce re-renders (update every 100ms instead of every frame)
  const currentTimeFloor = Math.floor(currentTime * 10);
  const throttledCurrentTime = useMemo(() => {
    return currentTimeFloor / 10; // Round to 100ms precision
  }, [currentTimeFloor]);

  // Use the active file's speaker map directly (Editor.tsx now passes file-specific metadata via caseAudioMetadata)
  const effectiveSpeakerMap = useMemo(() => {
    if (caseAudioMetadata?.speakerMap) {
      return caseAudioMetadata.speakerMap;
    }
    return undefined;
  }, [caseAudioMetadata?.speakerMap]);

  // Resolve a speaker label to its display name (custom name or "Speaker N")
  const getSpeakerDisplayName = useCallback(
    (speakerLabel: string) => {
      if (!speakerLabel) {
        return "Speaker 1";
      }
      if (effectiveSpeakerMap?.[speakerLabel]?.name) {
        return effectiveSpeakerMap[speakerLabel].name;
      }
      const speakerNumber = speakerLabel.replace(/\D/g, "");
      return `Speaker ${speakerNumber ? parseInt(speakerNumber) + 1 : 1}`;
    },
    [effectiveSpeakerMap]
  );

  // Memoized data for Whisper format
  const whisperData = useMemo(() => {
    if (!isWhisperFormat(transcription)) return null;
    return {
      words: transcription.words || [],
      text: transcription.text || "",
    };
  }, [transcription]);

  // Memoized data for Amazon Transcribe format
  const transcribeData = useMemo(() => {
    if (!isTranscribeFormat(transcription)) return null;

    const audioSegments = transcription.results.audio_segments || [];
    const segmentSpeakers = [
      ...new Set(audioSegments.map((segment) => segment.speaker_label)),
    ];

    // Also include speakers that exist in the metadata but may not be currently used in segments
    // Use effectiveSpeakerMap which prioritizes case_files metadata over caseAudioMetadata
    const metadataSpeakers = effectiveSpeakerMap
      ? Object.keys(effectiveSpeakerMap)
      : [];

    // Combine and deduplicate speakers from both sources
    const allSpeakers = [
      ...new Set([...segmentSpeakers, ...metadataSpeakers]),
    ].sort();

    const speakerColors = [
      "bg-blue-500",
      "bg-green-500",
      "bg-purple-500",
      "bg-orange-500",
      "bg-red-500",
      "bg-pink-500",
    ];
    const speakerMap = allSpeakers.reduce((map, speaker, index) => {
      // Always use custom metadata if available, don't fall back to generic labels
      // Use effectiveSpeakerMap which prioritizes case_files metadata
      const customSpeakerData = effectiveSpeakerMap?.[speaker];

      map[speaker] = {
        label: customSpeakerData?.name || `S${index + 1}`,
        color: speakerColors[index % speakerColors.length],
      };
      return map;
    }, {} as Record<string, { label: string; color: string }>);

    return { audioSegments, speakerMap, totalSpeakers: allSpeakers.length };
  }, [transcription, effectiveSpeakerMap]);

  // Whisper segments processing - group words into segments with breaks
  const whisperSegments = useMemo(() => {
    if (!whisperData) return [];

    const { words, text } = whisperData;
    if (!text || !words.length) return [];

    let segments: {
      words: Array<{
        word: string;
        start: number;
        end: number;
        text: string;
      }>;
      start: number;
      end: number;
    }[] = [];
    let currentPosition = 0;
    let currentSegmentWords: Array<{
      word: string;
      start: number;
      end: number;
      text: string;
    }> = [];

    const flushCurrentSegment = () => {
      if (currentSegmentWords.length > 0) {
        segments.push({
          words: [...currentSegmentWords],
          start: currentSegmentWords[0].start,
          end: currentSegmentWords[currentSegmentWords.length - 1].end,
        });
        currentSegmentWords = [];
      }
    };

    words.forEach((word, idx) => {
      const searchText = word.word.toLowerCase();
      const textToSearch = text.toLowerCase().slice(currentPosition);
      const relativeIndex = textToSearch.indexOf(searchText);

      if (relativeIndex !== -1) {
        const absoluteIndex = currentPosition + relativeIndex;
        let wordEndIndex = absoluteIndex + word.word.length;

        // Look ahead for punctuation and include it in the word segment
        while (
          wordEndIndex < text.length &&
          /[.,!?]/.test(text[wordEndIndex])
        ) {
          wordEndIndex++;
        }

        // Look ahead for spaces and include them in the word segment
        while (wordEndIndex < text.length && text[wordEndIndex] === " ") {
          wordEndIndex++;
        }

        let actualText = text.slice(absoluteIndex, wordEndIndex);
        const endsWithPeriod = /[.]/.test(actualText);

        // Calculate time gap to next word
        const nextWord = words[idx + 1];
        const timeGap = nextWord ? nextWord.start - word.end : 0;
        const shouldBreak = endsWithPeriod && timeGap > 3;

        // Add current word to the segment
        currentSegmentWords.push({
          word: word.word,
          start: word.start,
          end: word.end,
          text: actualText,
        });

        // If this word should cause a break or it's the last word, flush the segment
        if (shouldBreak || idx === words.length - 1) {
          flushCurrentSegment();
        }

        currentPosition = wordEndIndex;
      }
    });

    return segments;
  }, [whisperData]);

  // Find (Cmd/Ctrl+F). Read-only: no replace. Searches the transcript data
  // rather than the DOM because Virtuoso only mounts the segments in view;
  // matches are keyed by Virtuoso item index (segment index + 1, after the
  // disclaimer) and navigation scrolls the virtual list to them.
  const [findTerm, setFindTerm] = useState("");
  const [findCaseSensitive, setFindCaseSensitive] = useState(false);
  const [findIndex, setFindIndex] = useState(0);
  const [findScrollTick, setFindScrollTick] = useState(0);
  const findInputRef = useRef<HTMLInputElement>(null);
  const jumpToFirstMatchRef = useRef(false);

  const segmentTexts = useMemo(() => {
    if (whisperData) {
      return whisperSegments.map((segment) =>
        segment.words.map((w) => w.text).join("")
      );
    }
    if (transcribeData) {
      return transcribeData.audioSegments.map((segment) => segment.transcript || "");
    }
    return [];
  }, [whisperData, whisperSegments, transcribeData]);

  const findMatches = useMemo(() => {
    const matches: Array<{ itemIndex: number; start: number; end: number }> = [];
    if (!findTerm) return matches;
    const needle = findCaseSensitive ? findTerm : findTerm.toLowerCase();
    segmentTexts.forEach((text, segmentIndex) => {
      const haystack = findCaseSensitive ? text : text.toLowerCase();
      let at = haystack.indexOf(needle);
      while (at !== -1) {
        matches.push({
          itemIndex: segmentIndex + 1,
          start: at,
          end: at + needle.length,
        });
        at = haystack.indexOf(needle, at + needle.length);
      }
    });
    return matches;
  }, [segmentTexts, findTerm, findCaseSensitive]);

  const findRangesByItem = useMemo(() => {
    const byItem = new Map<number, FindRange[]>();
    findMatches.forEach((match, i) => {
      const ranges = byItem.get(match.itemIndex) ?? [];
      ranges.push({ start: match.start, end: match.end, current: i === findIndex });
      byItem.set(match.itemIndex, ranges);
    });
    return byItem;
  }, [findMatches, findIndex]);

  const goToFindMatch = useCallback(
    (index: number) => {
      const match = findMatches[index];
      if (!match) return;
      setFindIndex(index);
      // Following would drag the list back to the playhead
      setIsFollowing?.(false);
      virtuosoRef.current?.scrollToIndex({
        index: match.itemIndex,
        align: "center",
      });
      setFindScrollTick((t) => t + 1);
    },
    [findMatches, setIsFollowing]
  );

  // A new term (or case toggle) jumps to the first match once it's computed;
  // later recomputes (segment edits, speaker renames) keep the position.
  useEffect(() => {
    if (jumpToFirstMatchRef.current) {
      jumpToFirstMatchRef.current = false;
      if (findMatches.length) goToFindMatch(0);
      else setFindIndex(0);
    } else if (findIndex >= findMatches.length && findIndex !== 0) {
      setFindIndex(0);
    }
  }, [findMatches, findIndex, goToFindMatch]);

  // scrollToIndex centers the segment; a long segment can still leave the
  // match itself off screen, so nudge it into view once the item renders.
  useEffect(() => {
    if (!findScrollTick) return;
    const timer = setTimeout(() => {
      containerRef.current
        ?.querySelector('[data-find-current="true"]')
        ?.scrollIntoView({ block: "nearest" });
    }, 150);
    return () => clearTimeout(timer);
  }, [findScrollTick]);

  const handleFindSearch = useCallback((term: string) => {
    setFindTerm(term);
    jumpToFirstMatchRef.current = true;
  }, []);

  const handleFindNext = useCallback(() => {
    if (!findMatches.length) return;
    goToFindMatch((findIndex + 1) % findMatches.length);
  }, [findMatches.length, findIndex, goToFindMatch]);

  const handleFindPrevious = useCallback(() => {
    if (!findMatches.length) return;
    goToFindMatch((findIndex - 1 + findMatches.length) % findMatches.length);
  }, [findMatches.length, findIndex, goToFindMatch]);

  const handleToggleFindCaseSensitive = useCallback(() => {
    setFindCaseSensitive((v) => !v);
    jumpToFirstMatchRef.current = true;
  }, []);

  const handleCloseFind = useCallback(() => {
    setFindOpen?.(false);
    setFindTerm("");
    setFindCaseSensitive(false);
    setFindIndex(0);
  }, [setFindOpen]);

  useEffect(() => {
    if (!showFind) handleCloseFind();
  }, [showFind, handleCloseFind]);

  useEffect(() => {
    if (findDisabled) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "f") {
        e.preventDefault();
        setFindOpen?.(true);
        // Already open: put the cursor back in the box, like the browser
        setTimeout(() => findInputRef.current?.select(), 0);
      } else if (e.key === "Escape" && showFind) {
        handleCloseFind();
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [findDisabled, showFind, setFindOpen, handleCloseFind]);

  const formatTime = useCallback((timeStr: string) => {
    const time = parseFloat(timeStr);
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }, []);

  const parseTimeString = useCallback((timeString: string): number => {
    // Handle MM:SS format
    if (timeString.includes(":")) {
      const [minutes, seconds] = timeString
        .split(":")
        .map((num) => parseFloat(num));
      return minutes * 60 + seconds;
    }
    // Handle raw seconds format
    return parseFloat(timeString);
  }, []);

  const formatTimeForEdit = useCallback((timeStr: string): string => {
    const time = parseFloat(timeStr);
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, "0")}`;
  }, []);

  const handleRegenerateNarrative = async () => {
    if (!transcription || !caseId) {
      toast.error("No transcription or case ID available", { duration: 5000 });
      return;
    }

    setIsRegenerating(true);
    onRegenerateStart?.();
    confirmDialogRef.current?.close();

    try {
      // For AWS Transcribe format, only send audio_segments to reduce payload size
      let transcriptionPayload:
        | typeof transcription
        | {
            results: {
              audio_segments: Array<{
                id: number;
                transcript: string;
                start_time: string;
                end_time: string;
                speaker_label: string;
                items: number[];
              }>;
            };
          }
        | {
            text: string;
          } = transcription;

      if (isTranscribeFormat(transcription)) {
        transcriptionPayload = {
          results: {
            audio_segments: transcription.results.audio_segments || [],
          },
        };
      }

      if (isWhisperFormat(transcription)) {
        transcriptionPayload = {
          text: transcription.text,
        };
      }

      const response = await axios.post(
        "/api/regenerate-narrative",
        {
          caseId,
          transcription: transcriptionPayload,
        },
        {
          withCredentials: true,
        }
      );

      if (response.data.success) {
        toast.success("Narrative regeneration started.", {
          duration: 5000,
        });

        handleSelectedCase(caseId);
      }
    } catch (error) {
      console.error("Error regenerating narrative:", error);
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.error || "Error regenerating narrative"
        : "An unexpected error occurred";

      toast.error(errorMessage, {
        duration: 5000,
      });
    } finally {
      setIsRegenerating(false);
      onRegenerateEnd?.();
    }
  };

  const handleSaveSegmentEdit = useCallback(
    async (segmentId: number, newText: string) => {
      if (!transcription || !caseId || !isTranscribeFormat(transcription)) {
        toast.error("Unable to save edit", { duration: 3000 });
        return;
      }

      setIsSavingEdit(true);
      try {
        const response = await axios.patch(
          "/api/transcription/edit-segment",
          {
            caseId,
            fileId,
            segmentId,
            newText,
          },
          {
            withCredentials: true,
          }
        );

        if (response.data.success) {
          // Update the local transcription data
          const updatedTranscription = {
            ...transcription,
            results: {
              ...transcription.results,
              audio_segments: transcription.results.audio_segments.map(
                (segment) =>
                  segment.id === segmentId
                    ? { ...segment, transcript: newText }
                    : segment
              ),
            },
          };

          // Update the transcription in the parent component
          setCaseTranscription?.(updatedTranscription);

          toast.success("Transcription updated successfully", {
            duration: 3000,
          });
          setEditingSegmentId(null);
          setEditingText("");
        }
      } catch (error) {
        console.error("Error saving transcription edit:", error);
        const errorMessage = axios.isAxiosError(error)
          ? error.response?.data?.error || "Error saving transcription edit"
          : "An unexpected error occurred";

        toast.error(errorMessage, {
          duration: 5000,
        });
      } finally {
        setIsSavingEdit(false);
      }
    },
    [transcription, caseId, setCaseTranscription]
  );

  const handleSaveSpeakerChange = useCallback(
    async (segmentId: number, newSpeakerLabel: string) => {
      if (!transcription || !caseId || !isTranscribeFormat(transcription)) {
        toast.error("Unable to change speaker", { duration: 3000 });
        return;
      }

      setSavingSpeakerSegmentId(segmentId);
      try {
        const response = await axios.patch(
          "/api/transcription/change-speaker",
          {
            caseId,
            fileId,
            segmentId,
            newSpeakerLabel,
          },
          {
            withCredentials: true,
          }
        );

        if (response.data.success) {
          // Update the local transcription data
          const updatedTranscription = {
            ...transcription,
            results: {
              ...transcription.results,
              audio_segments: transcription.results.audio_segments.map(
                (segment) =>
                  segment.id === segmentId
                    ? { ...segment, speaker_label: newSpeakerLabel }
                    : segment
              ),
            },
          };

          // Update the transcription in the parent component
          setCaseTranscription?.(updatedTranscription);

          toast.success("Speaker changed successfully", { duration: 3000 });
        }
      } catch (error) {
        console.error("Error changing speaker:", error);
        const errorMessage = axios.isAxiosError(error)
          ? error.response?.data?.error || "Error changing speaker"
          : "An unexpected error occurred";

        toast.error(errorMessage, {
          duration: 5000,
        });
      } finally {
        setSavingSpeakerSegmentId(null);
      }
    },
    [transcription, caseId, setCaseTranscription]
  );

  const handleMergeSegments = useCallback(
    async (segmentId: number) => {
      if (!transcription || !caseId || !isTranscribeFormat(transcription)) {
        toast.error("Unable to merge segments", { duration: 3000 });
        return;
      }

      const segments = transcription.results.audio_segments;
      const currentSegmentIndex = segments.findIndex(
        (segment) => segment.id === segmentId
      );

      if (currentSegmentIndex <= 0) {
        toast.error("Cannot merge with previous segment", { duration: 3000 });
        return;
      }

      const currentSegment = segments[currentSegmentIndex];
      const previousSegment = segments[currentSegmentIndex - 1];

      setMergingUpSegmentId(segmentId);
      try {
        const response = await axios.patch(
          "/api/transcription/merge-segment-up",
          {
            caseId,
            fileId,
            segmentId: currentSegment.id,
            previousSegmentId: previousSegment.id,
          },
          {
            withCredentials: true,
          }
        );

        if (response.data.success) {
          // Update the local transcription data
          const mergedTranscript = `${previousSegment.transcript} ${currentSegment.transcript}`;

          const updatedTranscription = {
            ...transcription,
            results: {
              ...transcription.results,
              audio_segments: transcription.results.audio_segments
                .filter((segment) => segment.id !== currentSegment.id)
                .map((segment) =>
                  segment.id === previousSegment.id
                    ? {
                        ...segment,
                        transcript: mergedTranscript,
                        end_time: currentSegment.end_time,
                        items: [...segment.items, ...currentSegment.items],
                      }
                    : segment
                ),
            },
          };

          // Update the transcription in the parent component
          setCaseTranscription?.(updatedTranscription);

          toast.success("Segments merged successfully", { duration: 3000 });
        }
      } catch (error) {
        console.error("Error merging segments:", error);
        const errorMessage = axios.isAxiosError(error)
          ? error.response?.data?.error || "Error merging segments"
          : "An unexpected error occurred";

        toast.error(errorMessage, {
          duration: 5000,
        });
      } finally {
        setMergingUpSegmentId(null);
      }
    },
    [transcription, caseId, setCaseTranscription]
  );

  const handleMergeSegmentsDown = useCallback(
    async (segmentId: number) => {
      if (!transcription || !caseId || !isTranscribeFormat(transcription)) {
        toast.error("Unable to merge segments", { duration: 3000 });
        return;
      }

      const segments = transcription.results.audio_segments;
      const currentSegmentIndex = segments.findIndex(
        (segment) => segment.id === segmentId
      );

      if (currentSegmentIndex >= segments.length - 1) {
        toast.error("Cannot merge with next segment", { duration: 3000 });
        return;
      }

      const currentSegment = segments[currentSegmentIndex];
      const nextSegment = segments[currentSegmentIndex + 1];

      setMergingDownSegmentId(segmentId);
      try {
        const response = await axios.patch(
          "/api/transcription/merge-segment-down",
          {
            caseId,
            fileId,
            segmentId: currentSegment.id,
            nextSegmentId: nextSegment.id,
          },
          {
            withCredentials: true,
          }
        );

        if (response.data.success) {
          // Update the local transcription data
          const mergedTranscript = `${currentSegment.transcript} ${nextSegment.transcript}`;

          const updatedTranscription = {
            ...transcription,
            results: {
              ...transcription.results,
              audio_segments: transcription.results.audio_segments
                .filter((segment) => segment.id !== nextSegment.id)
                .map((segment) =>
                  segment.id === currentSegment.id
                    ? {
                        ...segment,
                        transcript: mergedTranscript,
                        end_time: nextSegment.end_time,
                        items: [...segment.items, ...nextSegment.items],
                      }
                    : segment
                ),
            },
          };

          // Update the transcription in the parent component
          setCaseTranscription?.(updatedTranscription);

          toast.success("Segments merged successfully", { duration: 3000 });
        }
      } catch (error) {
        console.error("Error merging segments:", error);
        const errorMessage = axios.isAxiosError(error)
          ? error.response?.data?.error || "Error merging segments"
          : "An unexpected error occurred";

        toast.error(errorMessage, {
          duration: 5000,
        });
      } finally {
        setMergingDownSegmentId(null);
      }
    },
    [transcription, caseId, setCaseTranscription]
  );

  const handleSplitSegment = useCallback(
    async (segmentId: number) => {
      if (!transcription || !caseId || !isTranscribeFormat(transcription)) {
        toast.error("Unable to split segment", { duration: 3000 });
        return;
      }

      // Show the split interface instead of automatically splitting
      setShowSplitInterface(segmentId);
    },
    [transcription, caseId]
  );

  const handleSplitAtWord = useCallback(
    async (segmentId: number, splitWordIndex: number) => {
      if (!transcription || !caseId || !isTranscribeFormat(transcription)) {
        toast.error("Unable to split segment", { duration: 3000 });
        return;
      }

      const segments = transcription.results.audio_segments;
      const currentSegment = segments.find(
        (segment) => segment.id === segmentId
      );

      if (!currentSegment) {
        toast.error("Segment not found", { duration: 3000 });
        return;
      }

      const words = currentSegment.transcript.trim().split(/\s+/);

      if (
        words.length < 2 ||
        splitWordIndex <= 0 ||
        splitWordIndex >= words.length
      ) {
        toast.error("Invalid split position", { duration: 3000 });
        return;
      }

      const firstHalf = words.slice(0, splitWordIndex).join(" ");
      const secondHalf = words.slice(splitWordIndex).join(" ");

      // Calculate the split time based on word position
      const startTime = parseFloat(currentSegment.start_time);
      const endTime = parseFloat(currentSegment.end_time);
      const totalDuration = endTime - startTime;
      const splitRatio = splitWordIndex / words.length;
      const splitTime = startTime + totalDuration * splitRatio;

      setSplittingSegmentId(segmentId);
      try {
        const response = await axios.patch(
          "/api/transcription/split-segment",
          {
            caseId,
            fileId,
            segmentId,
            splitTime: splitTime.toString(),
            firstHalfText: firstHalf,
            secondHalfText: secondHalf,
          },
          {
            withCredentials: true,
          }
        );

        if (response.data.success) {
          // Update the local transcription data
          const { newSegmentId } = response.data;

          const updatedTranscription = {
            ...transcription,
            results: {
              ...transcription.results,
              audio_segments: transcription.results.audio_segments
                .map((segment) => {
                  if (segment.id === segmentId) {
                    return {
                      ...segment,
                      transcript: firstHalf,
                      end_time: splitTime.toString(),
                    };
                  }
                  return segment;
                })
                .concat([
                  {
                    id: newSegmentId,
                    transcript: secondHalf,
                    start_time: splitTime.toString(),
                    end_time: currentSegment.end_time,
                    speaker_label: currentSegment.speaker_label,
                    items: [],
                  },
                ])
                .sort(
                  (a, b) => parseFloat(a.start_time) - parseFloat(b.start_time)
                ),
            },
          };

          setCaseTranscription?.(updatedTranscription);
          toast.success("Segment split successfully", { duration: 3000 });
          setShowSplitInterface(null);
        }
      } catch (error) {
        console.error("Error splitting segment:", error);
        const errorMessage = axios.isAxiosError(error)
          ? error.response?.data?.error || "Error splitting segment"
          : "An unexpected error occurred";

        toast.error(errorMessage, {
          duration: 5000,
        });
      } finally {
        setSplittingSegmentId(null);
      }
    },
    [transcription, caseId, setCaseTranscription]
  );

  const handleDeleteSegment = useCallback(
    async (segmentId: number) => {
      if (!transcription || !caseId || !isTranscribeFormat(transcription)) {
        toast.error("Unable to delete segment", { duration: 3000 });
        return;
      }

      const segments = transcription.results.audio_segments;
      const segmentToDeleteIndex = segments.findIndex(
        (segment) => segment.id === segmentId
      );

      if (segmentToDeleteIndex === -1) {
        toast.error("Segment not found", { duration: 3000 });
        return;
      }

      const segmentToDelete = segments[segmentToDeleteIndex];

      setDeletingSegmentId(segmentId);
      try {
        const response = await axios.patch(
          "/api/transcription/delete-segment",
          {
            caseId,
            fileId,
            segmentId,
          },
          {
            withCredentials: true,
          }
        );

        if (response.data.success) {
          // Update the local transcription data
          const updatedSegments = transcription.results.audio_segments.filter(
            (segment) => segment.id !== segmentId
          );

          // Update the previous segment's end time if it exists
          if (segmentToDeleteIndex > 0) {
            updatedSegments[segmentToDeleteIndex - 1] = {
              ...updatedSegments[segmentToDeleteIndex - 1],
              end_time: segmentToDelete.end_time,
            };
          }

          const updatedTranscription = {
            ...transcription,
            results: {
              ...transcription.results,
              audio_segments: updatedSegments,
            },
          };

          // Update the transcription in the parent component
          setCaseTranscription?.(updatedTranscription);

          toast.success("Segment deleted successfully", { duration: 3000 });
        }
      } catch (error) {
        console.error("Error deleting segment:", error);
        const errorMessage = axios.isAxiosError(error)
          ? error.response?.data?.error || "Error deleting segment"
          : "An unexpected error occurred";

        toast.error(errorMessage, {
          duration: 5000,
        });
      } finally {
        setDeletingSegmentId(null);
      }
    },
    [transcription, caseId, setCaseTranscription]
  );

  const handleStartTimestampEdit = useCallback(
    (segmentId: number, currentTime: string) => {
      setEditingTimestampId(segmentId);
      setEditingTimestamp(formatTimeForEdit(currentTime));
    },
    [formatTimeForEdit]
  );

  const handleCancelTimestampEdit = useCallback(() => {
    setEditingTimestampId(null);
    setEditingTimestamp("");
  }, []);

  const validateTimestamp = useCallback(
    (segmentId: number, newTime: string): boolean => {
      if (!transcription || !isTranscribeFormat(transcription)) return false;

      const segments = transcription.results.audio_segments;
      const currentSegmentIndex = segments.findIndex(
        (segment) => segment.id === segmentId
      );

      if (currentSegmentIndex === -1) return false;

      const newTimeFloat = parseTimeString(newTime);
      if (isNaN(newTimeFloat) || newTimeFloat < 0) return false;

      // Check against previous segment
      if (currentSegmentIndex > 0) {
        const previousSegment = segments[currentSegmentIndex - 1];
        const previousStartTime = parseFloat(previousSegment.start_time);
        if (newTimeFloat <= previousStartTime) {
          return false;
        }
      }

      // Check against next segment
      if (currentSegmentIndex < segments.length - 1) {
        const nextSegment = segments[currentSegmentIndex + 1];
        const nextStartTime = parseFloat(nextSegment.start_time);
        if (newTimeFloat >= nextStartTime) {
          return false;
        }
      }

      // Check against current segment's end time
      const currentSegment = segments[currentSegmentIndex];
      const currentEndTime = parseFloat(currentSegment.end_time);
      if (newTimeFloat >= currentEndTime) {
        return false;
      }

      return true;
    },
    [transcription, parseTimeString]
  );

  const handleSaveTimestamp = useCallback(
    async (segmentId: number, newTime: string) => {
      if (!transcription || !caseId || !isTranscribeFormat(transcription)) {
        toast.error("Unable to save timestamp", { duration: 3000 });
        return;
      }

      if (!validateTimestamp(segmentId, newTime)) {
        toast.error(
          "Invalid timestamp. Must be between adjacent segments and before end time.",
          { duration: 3000 }
        );
        return;
      }

      setIsSavingTimestamp(true);
      try {
        const newTimeInSeconds = parseTimeString(newTime);
        const response = await axios.patch(
          "/api/transcription/edit-timestamp",
          {
            caseId,
            fileId,
            segmentId,
            newStartTime: newTimeInSeconds.toString(),
          },
          {
            withCredentials: true,
          }
        );

        if (response.data.success) {
          // Update the local transcription data
          const segments = transcription.results.audio_segments;
          const currentSegmentIndex = segments.findIndex(
            (s) => s.id === segmentId
          );

          const updatedTranscription = {
            ...transcription,
            results: {
              ...transcription.results,
              audio_segments: transcription.results.audio_segments.map(
                (segment, index) => {
                  if (segment.id === segmentId) {
                    // Update the current segment's start time
                    return {
                      ...segment,
                      start_time: newTimeInSeconds.toString(),
                    };
                  } else if (index === currentSegmentIndex - 1) {
                    // Update the previous segment's end time
                    return {
                      ...segment,
                      end_time: newTimeInSeconds.toString(),
                    };
                  } else {
                    return segment;
                  }
                }
              ),
            },
          };

          // Update the transcription in the parent component
          setCaseTranscription?.(updatedTranscription);

          toast.success("Timestamp updated successfully", { duration: 3000 });
          setEditingTimestampId(null);
          setEditingTimestamp("");
        }
      } catch (error) {
        console.error("Error saving timestamp:", error);
        const errorMessage = axios.isAxiosError(error)
          ? error.response?.data?.error || "Error saving timestamp"
          : "An unexpected error occurred";

        toast.error(errorMessage, {
          duration: 5000,
        });
      } finally {
        setIsSavingTimestamp(false);
      }
    },
    [
      transcription,
      caseId,
      setCaseTranscription,
      validateTimestamp,
      parseTimeString,
    ]
  );

  const handleStartSpeakerNameEdit = useCallback(
    (speakerLabel: string) => {
      setEditingSpeakerNameId(speakerLabel);
      // Use effectiveSpeakerMap which prioritizes case_files metadata
      const currentName = effectiveSpeakerMap?.[speakerLabel]?.name || "";
      const currentInitials =
        effectiveSpeakerMap?.[speakerLabel]?.initials || "";
      setEditingSpeakerName(currentName);
      setEditingSpeakerInitials(currentInitials);
      speakerEditDialogRef.current?.showModal();
    },
    [effectiveSpeakerMap]
  );

  const handleCancelSpeakerNameEdit = useCallback(() => {
    setEditingSpeakerNameId(null);
    setEditingSpeakerName("");
    setEditingSpeakerInitials("");
    speakerEditDialogRef.current?.close();
  }, []);

  const handleSaveSpeakerName = useCallback(
    async (speakerLabel: string) => {
      if (!caseId || !editingSpeakerName.trim()) {
        toast.error("Speaker name cannot be empty", { duration: 3000 });
        return;
      }

      setIsSavingSpeakerName(true);
      try {
        // Generate initials from name if not provided
        let finalInitials = editingSpeakerInitials.trim();
        if (!finalInitials) {
          const words = editingSpeakerName.trim().split(/\s+/);
          if (words.length >= 2) {
            finalInitials = (words[0][0] + words[1][0]).toUpperCase();
          } else {
            finalInitials = editingSpeakerName
              .trim()
              .substring(0, 2)
              .toUpperCase();
          }
        }

        const response = await axios.patch(
          "/api/cases/speaker-metadata/update",
          {
            caseId,
            fileId,
            speakerLabel,
            name: editingSpeakerName.trim(),
            initials: finalInitials,
          },
          {
            withCredentials: true,
          }
        );

        if (response.data.success) {
          // Update the local audio metadata
          const updatedMetadata = {
            ...caseAudioMetadata!,
            speakerMap: {
              ...caseAudioMetadata?.speakerMap,
              [speakerLabel]: {
                name: editingSpeakerName.trim(),
                initials: finalInitials,
              },
            },
          };

          setCaseAudioMetadata?.(updatedMetadata);
          toast.success("Speaker name updated successfully", {
            duration: 3000,
          });
          handleCancelSpeakerNameEdit();
        }
      } catch (error) {
        console.error("Error saving speaker name:", error);
        const errorMessage = axios.isAxiosError(error)
          ? error.response?.data?.error || "Error saving speaker name"
          : "An unexpected error occurred";

        toast.error(errorMessage, {
          duration: 5000,
        });
      } finally {
        setIsSavingSpeakerName(false);
      }
    },
    [
      caseId,
      editingSpeakerName,
      editingSpeakerInitials,
      caseAudioMetadata,
      setCaseAudioMetadata,
      handleCancelSpeakerNameEdit,
    ]
  );

  const handleStartEdit = useCallback(
    (segmentId: number, currentText: string) => {
      setEditingSegmentId(segmentId);
      setEditingText(currentText);
    },
    []
  );

  const handleCancelEdit = useCallback(() => {
    setEditingSegmentId(null);
    setEditingText("");
  }, []);

  const handleAddNewSpeaker = useCallback(
    async (segmentId: number) => {
      if (!transcription || !caseId || !isTranscribeFormat(transcription)) {
        toast.error("Unable to add new speaker", { duration: 3000 });
        return;
      }

      // Set the segment ID we're adding a speaker for and show the dialog
      setAddingSpeakerSegmentId(segmentId);
      setNewSpeakerName("");
      setNewSpeakerInitials("");
      addSpeakerDialogRef.current?.showModal();
    },
    [transcription, caseId]
  );

  const handleCancelNewSpeaker = useCallback(() => {
    setAddingSpeakerSegmentId(null);
    setNewSpeakerName("");
    setNewSpeakerInitials("");
    addSpeakerDialogRef.current?.close();
  }, []);

  const handleStartDeleteSpeaker = useCallback((speakerLabel: string) => {
    setDeletingSpeakerLabel(speakerLabel);
    setReplacementSpeakerLabel("");
    deleteSpeakerDialogRef.current?.showModal();
  }, []);

  const handleCancelDeleteSpeaker = useCallback(() => {
    setDeletingSpeakerLabel(null);
    setReplacementSpeakerLabel("");
    deleteSpeakerDialogRef.current?.close();
  }, []);

  const handleDeleteSpeaker = useCallback(async () => {
    if (
      !transcription ||
      !caseId ||
      !isTranscribeFormat(transcription) ||
      !deletingSpeakerLabel ||
      !replacementSpeakerLabel
    ) {
      toast.error("Unable to delete speaker", { duration: 3000 });
      return;
    }

    if (deletingSpeakerLabel === replacementSpeakerLabel) {
      toast.error("Cannot replace speaker with itself", { duration: 3000 });
      return;
    }

    setIsDeletingSpeaker(true);
    try {
      // Use the new bulk delete speaker endpoint
      const response = await axios.patch(
        "/api/transcription/delete-speaker",
        {
          caseId,
          fileId,
          deletingSpeakerLabel,
          replacementSpeakerLabel,
        },
        {
          withCredentials: true,
        }
      );

      if (response.data.success) {
        // Update the local transcription data - replace all occurrences at once
        let updatedTranscription = {
          ...transcription,
          results: {
            ...transcription.results,
            audio_segments: transcription.results.audio_segments.map(
              (segment) =>
                segment.speaker_label === deletingSpeakerLabel
                  ? { ...segment, speaker_label: replacementSpeakerLabel }
                  : segment
            ),
          },
        };

        // If speakers were renumbered, apply those changes too
        if (response.data.speakerRenameMapping) {
          const renameMap = response.data.speakerRenameMapping;
          updatedTranscription = {
            ...updatedTranscription,
            results: {
              ...updatedTranscription.results,
              audio_segments: updatedTranscription.results.audio_segments.map(
                (segment) =>
                  renameMap[segment.speaker_label]
                    ? {
                        ...segment,
                        speaker_label: renameMap[segment.speaker_label],
                      }
                    : segment
              ),
            },
          };
        }

        // Update the transcription in the parent component
        setCaseTranscription?.(updatedTranscription);

        // Update speaker metadata - handle both deletion and renaming
        if (caseAudioMetadata?.speakerMap) {
          let updatedMetadata = {
            ...caseAudioMetadata,
            speakerMap: {
              ...caseAudioMetadata.speakerMap,
            },
          } as AudioMetadataType;

          // Remove the deleted speaker from the map
          if (updatedMetadata.speakerMap) {
            delete updatedMetadata.speakerMap[deletingSpeakerLabel];
          }

          // Apply speaker renaming to metadata if needed
          if (
            response.data.speakerRenameMapping &&
            updatedMetadata.speakerMap
          ) {
            const renameMap = response.data.speakerRenameMapping;
            const renamedSpeakerMap: Record<
              string,
              { name: string; initials: string }
            > = {};

            Object.entries(updatedMetadata.speakerMap).forEach(
              ([oldLabel, data]) => {
                const newLabel = renameMap[oldLabel] || oldLabel;
                renamedSpeakerMap[newLabel] = data;
              }
            );

            updatedMetadata.speakerMap = renamedSpeakerMap;
          }

          setCaseAudioMetadata?.(updatedMetadata);
        }

        toast.success(
          `Speaker deleted successfully. Updated ${response.data.updatedSegmentsCount} segments.`,
          {
            duration: 3000,
          }
        );
        handleCancelDeleteSpeaker();
      }
    } catch (error) {
      console.error("Error deleting speaker:", error);
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.error || "Error deleting speaker"
        : "An unexpected error occurred";

      toast.error(errorMessage, {
        duration: 5000,
      });
    } finally {
      setIsDeletingSpeaker(false);
    }
  }, [
    transcription,
    caseId,
    deletingSpeakerLabel,
    replacementSpeakerLabel,
    caseAudioMetadata,
    setCaseAudioMetadata,
    setCaseTranscription,
    handleCancelDeleteSpeaker,
  ]);

  const handleSaveNewSpeaker = useCallback(async () => {
    if (
      !transcription ||
      !caseId ||
      !isTranscribeFormat(transcription) ||
      addingSpeakerSegmentId === null
    ) {
      toast.error("Unable to save new speaker", { duration: 3000 });
      return;
    }

    if (!newSpeakerName.trim()) {
      toast.error("Speaker name cannot be empty", { duration: 3000 });
      return;
    }

    setIsSavingNewSpeaker(true);
    try {
      const segments = transcription.results.audio_segments;
      const existingSpeakers = [
        ...new Set(segments.map((segment) => segment.speaker_label)),
      ].sort();

      // Generate a new speaker label that doesn't conflict with existing ones
      let newSpeakerNumber = 0;
      let newSpeakerLabel = `spk_${newSpeakerNumber}`;

      while (existingSpeakers.includes(newSpeakerLabel)) {
        newSpeakerNumber++;
        newSpeakerLabel = `spk_${newSpeakerNumber}`;
      }

      // First, change the speaker for this segment
      await handleSaveSpeakerChange(addingSpeakerSegmentId, newSpeakerLabel);

      // Then, save the speaker metadata with the custom name
      const response = await axios.patch(
        "/api/cases/speaker-metadata/update",
        {
          caseId,
          fileId,
          speakerLabel: newSpeakerLabel,
          name: newSpeakerName.trim(),
          initials: (() => {
            if (newSpeakerInitials.trim()) {
              return newSpeakerInitials.trim();
            }

            const words = newSpeakerName.trim().split(/\s+/);
            if (words.length >= 2) {
              return (words[0][0] + words[1][0]).toUpperCase();
            } else {
              return newSpeakerName.trim().substring(0, 2).toUpperCase();
            }
          })(),
        },
        {
          withCredentials: true,
        }
      );

      if (response.data.success) {
        // Update the local audio metadata - ensure we properly merge with existing speakers
        const finalInitials = (() => {
          if (newSpeakerInitials.trim()) {
            return newSpeakerInitials.trim();
          }

          const words = newSpeakerName.trim().split(/\s+/);
          if (words.length >= 2) {
            return (words[0][0] + words[1][0]).toUpperCase();
          } else {
            return newSpeakerName.trim().substring(0, 2).toUpperCase();
          }
        })();

        const updatedMetadata = {
          ...caseAudioMetadata,
          speakerMap: {
            ...caseAudioMetadata?.speakerMap,
            [newSpeakerLabel]: {
              name: newSpeakerName.trim(),
              initials: finalInitials,
            },
          },
        } as AudioMetadataType;

        setCaseAudioMetadata?.(updatedMetadata);
        toast.success("New speaker added successfully", {
          duration: 3000,
        });
        handleCancelNewSpeaker();
      }
    } catch (error) {
      console.error("Error saving new speaker:", error);
      const errorMessage = axios.isAxiosError(error)
        ? error.response?.data?.error || "Error saving new speaker"
        : "An unexpected error occurred";

      toast.error(errorMessage, {
        duration: 5000,
      });
    } finally {
      setIsSavingNewSpeaker(false);
    }
  }, [
    transcription,
    caseId,
    addingSpeakerSegmentId,
    newSpeakerName,
    newSpeakerInitials,
    handleSaveSpeakerChange,
    caseAudioMetadata,
    setCaseAudioMetadata,
    handleCancelNewSpeaker,
  ]);

  // Handle scroll events to detect manual scrolling
  const handleVirtuosoScroll = useCallback(() => {
    // Ignore scroll events that are triggered by auto-scroll
    if (isAutoScrollingRef.current) {
      return;
    }

    // Always disable following when user scrolls manually, regardless of current state
    if (setIsFollowing) {
      setIsFollowing(false);

      // Also reset the user clicked flag to prevent auto-scroll from resuming
      userClickedSegmentRef.current = false;

      // Clear any existing timeout
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    }
  }, [setIsFollowing]);

  const [copiedTranscription, setCopiedTranscription] = useState(false);
  const copyDialogRef = useRef<HTMLDialogElement>(null);
  const copyListRef = useRef<HTMLDivElement>(null);
  const [copySelectedSegmentIds, setCopySelectedSegmentIds] = useState<
    Set<number>
  >(new Set());
  // Persisted so the next copy defaults to the user's last choices
  const [copyIncludeSpeakers, setCopyIncludeSpeakers] = useLocalStorageState(
    "pnai-copyTranscriptionIncludeSpeakers",
    true
  );
  const [copyIncludeTimestamps, setCopyIncludeTimestamps] =
    useLocalStorageState("pnai-copyTranscriptionIncludeTimestamps", false);

  const handleCopyTranscription = useCallback(async () => {
    if (!transcription || !isWhisperFormat(transcription)) return;

    // Build from the displayed segments so paragraph breaks are preserved
    const textToCopy =
      whisperSegments.length > 0
        ? whisperSegments
            .map((segment) =>
              segment.words
                .map((word) => word.text)
                .join("")
                .trim()
            )
            .join("\n\n")
        : transcription.text || "";

    if (!textToCopy) return;

    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopiedTranscription(true);
      toast.success("Transcription copied to clipboard", { duration: 3000 });
      setTimeout(() => setCopiedTranscription(false), 2000);
    } catch (error) {
      console.error("Error copying transcription:", error);
      toast.error("Failed to copy transcription", { duration: 3000 });
    }
  }, [transcription, whisperSegments]);

  // Open the copy dialog for interaction (Transcribe) audio. If the user has
  // text selected in the transcription, pre-check only the segments that
  // intersect the selection; otherwise pre-check everything.
  const handleOpenCopyDialog = useCallback(() => {
    if (!isTranscribeFormat(transcription)) return;
    const segments = transcription.results.audio_segments || [];

    const selectedIds: number[] = [];
    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0 && !selection.isCollapsed) {
      const range = selection.getRangeAt(0);
      document
        .querySelectorAll("[data-segment-id]")
        .forEach((el) => {
          if (range.intersectsNode(el)) {
            selectedIds.push(Number(el.getAttribute("data-segment-id")));
          }
        });
    }

    const idsToCheck =
      selectedIds.length > 0
        ? new Set(selectedIds)
        : new Set(segments.map((segment) => segment.id));
    setCopySelectedSegmentIds(idsToCheck);
    copyDialogRef.current?.showModal();

    // Scroll the checklist to the first pre-checked segment (or back to the
    // top when everything is checked)
    const firstSelected =
      selectedIds.length > 0
        ? segments.find((segment) => idsToCheck.has(segment.id))
        : undefined;
    requestAnimationFrame(() => {
      const listEl = copyListRef.current;
      if (!listEl) return;
      const itemEl = firstSelected
        ? listEl.querySelector<HTMLElement>(
            `[data-copy-segment-id="${firstSelected.id}"]`
          )
        : null;
      if (itemEl) {
        // Offset for the sticky "Select all" header
        listEl.scrollTop =
          itemEl.getBoundingClientRect().top -
          listEl.getBoundingClientRect().top +
          listEl.scrollTop -
          40;
      } else {
        listEl.scrollTop = 0;
      }
    });
  }, [transcription]);

  // Intercept Cmd+C / Ctrl+C over the transcription (interaction audio):
  // instead of a plain copy, open the copy dialog pre-checked with the
  // highlighted segments
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isTranscribeFormat(transcription)) return;

    const handleCopyEvent = (e: ClipboardEvent) => {
      // Don't hijack copies from the dialog itself or from form fields
      if (copyDialogRef.current?.open) return;
      const target = e.target as HTMLElement | null;
      if (target?.closest("input, textarea, [contenteditable='true']")) return;

      const selection = window.getSelection();
      if (!selection || selection.rangeCount === 0 || selection.isCollapsed) {
        return;
      }

      // Only intercept when the highlight actually covers transcription
      // segments
      const range = selection.getRangeAt(0);
      const touchesSegment = Array.from(
        container.querySelectorAll("[data-segment-id]")
      ).some((el) => range.intersectsNode(el));
      if (!touchesSegment) return;

      e.preventDefault();
      handleOpenCopyDialog();
    };

    container.addEventListener("copy", handleCopyEvent);
    return () => container.removeEventListener("copy", handleCopyEvent);
  }, [transcription, handleOpenCopyDialog]);

  const copyPreviewText = useMemo(() => {
    if (!isTranscribeFormat(transcription)) return "";
    const segments = transcription.results.audio_segments || [];
    return segments
      .filter((segment) => copySelectedSegmentIds.has(segment.id))
      .map((segment) => {
        const parts: string[] = [];
        if (copyIncludeTimestamps) {
          parts.push(`[${formatTime(segment.start_time)}]`);
        }
        if (copyIncludeSpeakers) {
          parts.push(`${getSpeakerDisplayName(segment.speaker_label)}:`);
        }
        parts.push(segment.transcript);
        return parts.join(" ");
      })
      .join("\n\n");
  }, [
    transcription,
    copySelectedSegmentIds,
    copyIncludeSpeakers,
    copyIncludeTimestamps,
    formatTime,
    getSpeakerDisplayName,
  ]);

  const handleToggleCopySegment = useCallback((segmentId: number) => {
    setCopySelectedSegmentIds((prev) => {
      const next = new Set(prev);
      if (next.has(segmentId)) {
        next.delete(segmentId);
      } else {
        next.add(segmentId);
      }
      return next;
    });
  }, []);

  const handleCopyFromDialog = useCallback(async () => {
    if (!copyPreviewText) return;
    try {
      await navigator.clipboard.writeText(copyPreviewText);
      toast.success("Transcription copied to clipboard", { duration: 3000 });
      copyDialogRef.current?.close();
    } catch (error) {
      console.error("Error copying transcription:", error);
      toast.error("Failed to copy transcription", { duration: 3000 });
    }
  }, [copyPreviewText]);

  const handleCopySegment = useCallback(async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Transcription copied to clipboard", { duration: 3000 });
    } catch (error) {
      console.error("Error copying transcription:", error);
      toast.error("Failed to copy transcription", { duration: 3000 });
    }
  }, []);

  // Stable item content renderer to prevent re-renders
  const itemContent = useCallback(
    (index: number, item: any) => {
      if (item.type === "disclaimer") {
        if (isWhisperFormat(transcription)) {
          return (
            <div className="mb-4 mt-8">
              <div className="bg-blue-50 dark:bg-blue-900/40 p-2 rounded-lg">
                <p className="text-xs p-0 m-0">
                  This transcription was generated automatically and may contain
                  errors or inaccuracies. It is the responsibility of the
                  reviewing officer to verify the content and ensure its
                  accuracy before relying on it for official use.
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleCopyTranscription}
                className="w-full mt-2 flex items-center justify-center gap-2"
              >
                {copiedTranscription ? (
                  <CheckIcon className="w-4 h-4 text-green-600 dark:text-green-400" />
                ) : (
                  <ClipboardDocumentIcon className="w-4 h-4" />
                )}
                Copy transcription
              </Button>
            </div>
          );
        } else {
          return (
            <div className="mt-8">
              <div className="bg-blue-50 dark:bg-blue-900/40 p-2 rounded-lg">
                <p className="text-xs p-0 m-0">
                  Speaker labels in this transcription are generated
                  automatically and may not be 100% accurate. It is the
                  responsibility of the reviewing officer to verify and
                  differentiate between speakers to ensure the accuracy of the
                  narrative.
                </p>
              </div>
              <Button
                variant="secondary"
                size="sm"
                // Preserve any text selection in the transcription so the
                // dialog can pre-check the selected segments
                onMouseDown={(e) => e.preventDefault()}
                onClick={handleOpenCopyDialog}
                className="w-full mt-2 flex items-center justify-center gap-2"
              >
                <ClipboardDocumentIcon className="w-4 h-4" />
                Copy transcription
              </Button>
            </div>
          );
        }
      }

      if (item.type === "break") {
        return <div className="h-4" />; // Space for breaks
      }

      if (item.type === "segment" && item.data) {
        if (isWhisperFormat(transcription)) {
          const segment = item.data;
          const findRanges = findRangesByItem.get(index);
          let wordOffset = 0;
          return (
            <div className="rounded transition-colors duration-50 mb-2 leading-[1.5]">
              {segment.words.map((wordData: any, wordIndex: number) => {
                const offset = wordOffset;
                wordOffset += wordData.text.length;
                return (
                  <span
                    key={wordIndex}
                    onClick={() => {
                      if (wordData.text.trim()) {
                        userClickedSegmentRef.current = true;
                        onWordClick?.(wordData.start, true);
                        setIsFollowing?.(true);
                      }
                    }}
                    data-active={
                      throttledCurrentTime >= wordData.start - 0.01 &&
                      throttledCurrentTime <= wordData.end
                    }
                    className={`${
                      wordData.text.trim()
                        ? "cursor-pointer hover:bg-blue-100 dark:hover:bg-blue-900/60 data-[active=true]:bg-blue-100 dark:data-[active=true]:bg-blue-900/60"
                        : ""
                    } rounded transition-colors duration-50 inline`}
                  >
                    {highlightFindMatches(wordData.text, offset, findRanges)}
                  </span>
                );
              })}
            </div>
          );
        } else {
          // Transcribe format rendering - moved from renderTranscribeItem
          const segment = item.data;
          const { speakerMap } = transcribeData!;

          // Helper function to get speaker name - prioritizes case_files metadata
          const getSpeakerName = (speakerLabel: string) => {
            if (!speakerLabel) {
              return "Speaker 1";
            }
            // Use effectiveSpeakerMap which prioritizes case_files metadata over caseAudioMetadata
            if (effectiveSpeakerMap?.[speakerLabel]?.name) {
              return effectiveSpeakerMap[speakerLabel].name;
            }
            // Only fall back to generic names if no custom metadata exists
            const speakerNumber = speakerLabel.replace(/\D/g, "");
            const displayNumber = speakerNumber
              ? parseInt(speakerNumber) + 1
              : 1;
            return `Speaker ${displayNumber}`;
          };

          // Get all available speakers for the dropdown
          const availableSpeakers = Object.keys(speakerMap).map(
            (speakerLabel) => ({
              value: speakerLabel,
              label: getSpeakerName(speakerLabel),
            })
          );

          // Helper function to get speaker initials for the avatar - prioritizes case_files metadata
          const getSpeakerInitials = (speakerLabel: string) => {
            // Use effectiveSpeakerMap which prioritizes case_files metadata over caseAudioMetadata
            if (effectiveSpeakerMap?.[speakerLabel]?.initials) {
              return effectiveSpeakerMap[speakerLabel].initials;
            }
            // Default to the existing speakerMap label (S1, S2, etc.)
            return speakerMap[speakerLabel].label;
          };

          return (
            <div className="flex gap-3 group mt-4" data-segment-id={segment.id}>
              <div className="flex-shrink-0 flex flex-col items-center gap-1 mt-1">
                <div
                  className={`w-8 h-8 rounded-full ${
                    speakerMap[segment.speaker_label].color
                  } text-white text-sm font-medium flex items-center justify-center z-10`}
                >
                  {getSpeakerInitials(segment.speaker_label)}
                </div>
                {editingTimestampId === segment.id ? (
                  <>
                    <div className="flex flex-col items-center h-4 relative w-12">
                      {isSavingTimestamp && (
                        <div className="absolute right-[-10px] top-1/2 -translate-y-1/2 z-20">
                          <div className="w-3 h-3 border-2 border-base-300 border-t-blue-500 rounded-full animate-spin" />
                        </div>
                      )}
                      <input
                        type="text"
                        value={editingTimestamp}
                        onChange={(e) => setEditingTimestamp(e.target.value)}
                        className="absolute input input-xs border-0 focus:outline-none w-full h-[25px] text-center font-mono text-xs mt-[-5px] z-0 text-blue-600 dark:text-blue-300 p-0"
                        placeholder="M:SS"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleSaveTimestamp(segment.id, editingTimestamp);
                          } else if (e.key === "Escape") {
                            handleCancelTimestampEdit();
                          }
                        }}
                        onBlur={() => {
                          if (!isSavingTimestamp) {
                            handleCancelTimestampEdit();
                          }
                        }}
                      />
                    </div>
                  </>
                ) : (
                  <button
                    onClick={() =>
                      !isReadOnly &&
                      handleStartTimestampEdit(segment.id, segment.start_time)
                    }
                    disabled={isReadOnly}
                    className={`text-xs text-base-content/60 font-mono w-12 ${
                      !isReadOnly ? "hover:text-blue-500 dark:hover:text-blue-300 cursor-pointer" : ""
                    }`}
                    title={!isReadOnly ? "Click to edit timestamp" : ""}
                  >
                    {formatTime(segment.start_time)}
                  </button>
                )}
              </div>
              <div className="flex-1 min-w-0 relative">
                <div className="text-xs px-2 font-semibold text-gray-700 dark:text-base-content/80 group/speaker relative">
                  {!isReadOnly ? (
                    <div className="dropdown">
                      <button
                        tabIndex={0}
                        data-tour="transcript-speaker"
                        className="flex items-center gap-1 hover:text-blue-600 dark:hover:text-blue-300 cursor-pointer"
                        disabled={savingSpeakerSegmentId === segment.id}
                      >
                        {getSpeakerName(segment.speaker_label)}
                        {savingSpeakerSegmentId === segment.id ? (
                          <div className="w-3 h-3 border-2 border-base-300 border-t-blue-500 rounded-full animate-spin" />
                        ) : (
                          <ChevronDownIcon className="w-3 h-3 opacity-0 group-hover/speaker:opacity-100 transition-opacity duration-150" />
                        )}
                      </button>
                      <ul
                        tabIndex={0}
                        className="dropdown-content menu bg-base-100 rounded-lg z-[1] p-2 shadow dark:border dark:border-base-300 w-max min-w-48 mt-1"
                      >
                        {availableSpeakers.map((speaker) => (
                          <li
                            className="my-[1px] p-0 group/item"
                            key={speaker.value}
                          >
                            <div className="flex items-center justify-between hover:bg-blue-50 dark:hover:bg-blue-900/40 px-2 py-2 rounded relative">
                              <button
                                onClick={() => {
                                  if (speaker.value !== segment.speaker_label) {
                                    handleSaveSpeakerChange(
                                      segment.id,
                                      speaker.value
                                    );
                                  }
                                  // Close dropdown by removing focus
                                  (
                                    document.activeElement as HTMLElement
                                  )?.blur();
                                }}
                                className={cn(
                                  "text-left hover:text-blue-500 dark:hover:text-blue-300 flex-1",
                                  speaker.value === segment.speaker_label
                                    ? "font-semibold"
                                    : ""
                                )}
                                disabled={savingSpeakerSegmentId === segment.id}
                              >
                                {speaker.label}
                              </button>
                              <div className="flex gap-1">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleStartSpeakerNameEdit(speaker.value);
                                    // Close dropdown by removing focus
                                    (
                                      document.activeElement as HTMLElement
                                    )?.blur();
                                  }}
                                  className="opacity-0 group-hover/item:opacity-100 transition-opacity duration-150 p-1 hover:bg-blue-500 rounded-full group/edit"
                                  title={`Edit ${speaker.label} name`}
                                  data-tour="transcript-speaker-edit"
                                >
                                  <PencilIcon className="size-4 text-base-content/60 group-hover/edit:text-white" />
                                </button>
                                {(transcribeData?.totalSpeakers || 0) > 1 && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleStartDeleteSpeaker(speaker.value);
                                      // Close dropdown by removing focus
                                      (
                                        document.activeElement as HTMLElement
                                      )?.blur();
                                    }}
                                    className="opacity-0 group-hover/item:opacity-100 transition-opacity duration-150 p-1 hover:bg-red-500 rounded-full group/delete"
                                    title={`Delete ${speaker.label}`}
                                  >
                                    <TrashIcon className="size-4 text-base-content/60 group-hover/delete:text-white" />
                                  </button>
                                )}
                              </div>
                            </div>
                          </li>
                        ))}
                        <li className="my-[1px] p-0">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleAddNewSpeaker(segment.id);
                              // Close dropdown by removing focus
                              (document.activeElement as HTMLElement)?.blur();
                            }}
                            className="text-left hover:bg-blue-50 dark:hover:bg-blue-900/40 px-2 py-2 w-full flex items-center gap-2 text-blue-600 hover:text-blue-700 dark:text-blue-300 rounded"
                            disabled={savingSpeakerSegmentId === segment.id}
                          >
                            <PlusIcon className="size-3" />
                            Add New Speaker
                          </button>
                        </li>
                      </ul>
                    </div>
                  ) : (
                    <span>{getSpeakerName(segment.speaker_label)}</span>
                  )}
                </div>
                {editingSegmentId === segment.id ? (
                  <div className="mt-0">
                    <textarea
                      value={editingText}
                      onChange={(e) => setEditingText(e.target.value)}
                      className="w-full py-[3px] px-[7px] leading-[1.5] text-md border border-gray-300 dark:border-base-300 rounded resize-none"
                      rows={4}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                          handleSaveSegmentEdit(segment.id, editingText);
                        } else if (e.key === "Escape") {
                          handleCancelEdit();
                        }
                      }}
                    />
                    <div className="flex gap-1">
                      <Button
                        data-tour="transcript-segment-save"
                        onClick={() =>
                          handleSaveSegmentEdit(segment.id, editingText)
                        }
                        disabled={isSavingEdit}
                        variant="primary"
                        size="xs"
                      >
                        {isSavingEdit ? "Saving..." : "Save"}
                      </Button>
                      <Button
                        onClick={handleCancelEdit}
                        disabled={isSavingEdit}
                        variant="secondary"
                        size="xs"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : showSplitInterface === segment.id ? (
                  <div className="text-md leading-[1.5] py-1 px-2 transition-colors duration-150 relative">
                    <div className="flex flex-wrap items-center gap-0">
                      {segment.transcript
                        .trim()
                        .split(/\s+/)
                        .map((word, wordIndex) => (
                          <Fragment key={wordIndex}>
                            {wordIndex > 0 && (
                              <button
                                onClick={() =>
                                  handleSplitAtWord(segment.id, wordIndex)
                                }
                                className="w-1.5 h-[22px] bg-gray-300 dark:bg-base-300 hover:bg-blue-500 transition-colors duration-150 cursor-pointer mx-1 flex-shrink-0"
                                title={`Split here (before "${word}")`}
                                data-tour="transcript-split-point"
                                disabled={splittingSegmentId === segment.id}
                              />
                            )}
                            <span className="text-gray-700 dark:text-base-content/80 select-none">
                              {word}
                            </span>
                          </Fragment>
                        ))}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button
                        onClick={() => setShowSplitInterface(null)}
                        variant="secondary"
                        size="xs"
                        disabled={splittingSegmentId === segment.id}
                      >
                        Cancel
                      </Button>
                      <span className="text-xs text-base-content/60 flex items-center gap-2">
                        {splittingSegmentId === segment.id && (
                          <div className="w-3 h-3 border-2 border-base-300 border-t-blue-500 rounded-full animate-spin" />
                        )}
                        Click the gray lines between words to split the segment
                      </span>
                    </div>
                  </div>
                ) : (
                  <div
                    data-tour="transcript-segment"
                    className="text-md leading-[1.5] cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/40 data-[active=true]:bg-blue-100 dark:data-[active=true]:bg-blue-900/60 rounded py-1 px-2 transition-colors duration-150 relative"
                    onClick={() => {
                      userClickedSegmentRef.current = true;
                      onWordClick?.(parseFloat(segment.start_time), true);
                      setIsFollowing?.(true);
                    }}
                    data-active={
                      throttledCurrentTime >=
                        parseFloat(segment.start_time) - 0.1 &&
                      throttledCurrentTime <= parseFloat(segment.end_time)
                    }
                  >
                    {highlightFindMatches(
                      segment.transcript,
                      0,
                      findRangesByItem.get(index)
                    )}
                    {!isReadOnly && (
                      <div className="dropdown dropdown-end absolute right-2 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity duration-150 z-20">
                        <button
                          tabIndex={0}
                          className="p-0.5 hover:bg-blue-500 rounded-full group/actions bg-base-100/80 mt-1"
                          title="Segment actions"
                          data-tour="transcript-segment-menu"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <EllipsisVerticalIcon className="size-5 text-base-content/60 group-hover/actions:text-white" />
                        </button>
                        <ul
                          tabIndex={0}
                          className="dropdown-content menu bg-base-100 rounded-lg z-[1] w-52 p-2 shadow dark:border dark:border-base-300 mt-1"
                        >
                          <li className="p-0 m-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopySegment(
                                  `${getSpeakerName(segment.speaker_label)}: ${
                                    segment.transcript
                                  }`
                                );
                                // Close dropdown by removing focus
                                (document.activeElement as HTMLElement)?.blur();
                              }}
                              className="text-left hover:bg-blue-50 dark:hover:bg-blue-900/40 p-2 flex items-center gap-2"
                            >
                              <ClipboardDocumentIcon className="size-4" />
                              Copy Transcription
                            </button>
                          </li>
                          <li className="p-0 m-0">
                            <button
                              data-tour="transcript-segment-edit"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStartEdit(segment.id, segment.transcript);
                                // Close dropdown by removing focus
                                (document.activeElement as HTMLElement)?.blur();
                              }}
                              className="text-left hover:bg-blue-50 dark:hover:bg-blue-900/40 p-2 flex items-center gap-2"
                            >
                              <PencilIcon className="size-4" />
                              Edit Transcription
                            </button>
                          </li>
                          <li className="p-0 m-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMergeSegments(segment.id);
                                // Close dropdown by removing focus
                                (document.activeElement as HTMLElement)?.blur();
                              }}
                              className="text-left hover:bg-blue-50 dark:hover:bg-blue-900/40 p-2 flex items-center gap-2"
                              disabled={mergingUpSegmentId === segment.id}
                            >
                              {mergingUpSegmentId === segment.id ? (
                                <div className="size-4 border-2 border-base-300 border-t-blue-500 rounded-full animate-spin" />
                              ) : (
                                <ChevronDoubleUpIcon className="size-4" />
                              )}
                              Merge with Previous
                            </button>
                          </li>
                          <li className="p-0 m-0">
                            <button
                              data-tour="transcript-segment-merge-next"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleMergeSegmentsDown(segment.id);
                                // Close dropdown by removing focus
                                (document.activeElement as HTMLElement)?.blur();
                              }}
                              className="text-left hover:bg-blue-50 dark:hover:bg-blue-900/40 p-2 flex items-center gap-2"
                              disabled={mergingDownSegmentId === segment.id}
                            >
                              {mergingDownSegmentId === segment.id ? (
                                <div className="size-4 border-2 border-base-300 border-t-blue-500 rounded-full animate-spin" />
                              ) : (
                                <ChevronDoubleDownIcon className="size-4" />
                              )}
                              Merge with Next
                            </button>
                          </li>
                          {segment.transcript.trim().split(/\s+/).length >
                            1 && (
                            <li className="p-0 m-0">
                              <button
                                data-tour="transcript-segment-split"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setShowSplitInterface(segment.id);
                                  // Close dropdown by removing focus
                                  (
                                    document.activeElement as HTMLElement
                                  )?.blur();
                                }}
                                className="text-left hover:bg-blue-50 dark:hover:bg-blue-900/40 p-2 flex items-center gap-2"
                                disabled={splittingSegmentId === segment.id}
                              >
                                {splittingSegmentId === segment.id ? (
                                  <div className="w-4 h-4 border-2 border-base-300 border-t-blue-500 rounded-full animate-spin" />
                                ) : (
                                  <ArrowsUpDownIcon className="w-4 h-4" />
                                )}
                                Split Segment
                              </button>
                            </li>
                          )}
                          <li className="p-0 m-0">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSegment(segment.id);
                                // Close dropdown by removing focus
                                (document.activeElement as HTMLElement)?.blur();
                              }}
                              className="text-left hover:bg-red-50 dark:hover:bg-red-950/40 p-2 flex items-center gap-2 text-red-600 hover:text-red-700 dark:text-red-400"
                              disabled={deletingSegmentId === segment.id}
                            >
                              {deletingSegmentId === segment.id ? (
                                <div className="w-4 h-4 border-2 border-base-300 border-t-red-500 rounded-full animate-spin" />
                              ) : (
                                <TrashIcon className="w-4 h-4" />
                              )}
                              Delete Segment
                            </button>
                          </li>
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        }
      }

      return null;
    },
    [
      transcription,
      throttledCurrentTime,
      onWordClick,
      setIsFollowing,
      transcribeData,
      effectiveSpeakerMap,
      editingTimestampId,
      editingTimestamp,
      isSavingTimestamp,
      handleSaveTimestamp,
      handleCancelTimestampEdit,
      handleStartTimestampEdit,
      isReadOnly,
      formatTime,
      savingSpeakerSegmentId,
      handleSaveSpeakerChange,
      handleStartSpeakerNameEdit,
      editingSegmentId,
      editingText,
      setEditingText,
      handleSaveSegmentEdit,
      handleCancelEdit,
      isSavingEdit,
      handleStartEdit,
      mergingUpSegmentId,
      handleMergeSegments,
      mergingDownSegmentId,
      handleMergeSegmentsDown,
      splittingSegmentId,
      showSplitInterface,
      setShowSplitInterface,
      handleSplitAtWord,
      deletingSegmentId,
      handleDeleteSegment,
      handleAddNewSpeaker,
      handleStartDeleteSpeaker,
      handleCopyTranscription,
      handleOpenCopyDialog,
      handleCopySegment,
      copiedTranscription,
      findRangesByItem,
    ]
  );

  // Stable virtuoso components to prevent re-rendering
  const virtuosoComponents = useMemo(
    () => ({
      Item: ({ children, ...props }: any) => (
        <div {...props} className="">
          {children}
        </div>
      ),
      Footer: () => <div className="h-32" />,
    }),
    []
  );

  // Add virtualized rendering data structures
  const whisperVirtualData = useMemo(() => {
    if (!whisperSegments.length) return [];

    let items: Array<{
      type: "disclaimer" | "segment";
      data?: any;
      index?: number;
    }> = [];

    // Add disclaimer
    items.push({ type: "disclaimer" });

    // Add segments
    whisperSegments.forEach((segment, index) => {
      items.push({
        type: "segment",
        data: segment,
        index,
      });
    });

    return items;
  }, [whisperSegments]);

  const transcribeVirtualData = useMemo(() => {
    if (!transcribeData) return [];

    let items: Array<{
      type: "disclaimer" | "segment";
      data?: any;
      index?: number;
    }> = [];

    // Add disclaimer
    items.push({ type: "disclaimer" });

    // Add segments
    transcribeData.audioSegments.forEach((segment, index) => {
      items.push({
        type: "segment",
        data: segment,
        index,
      });
    });

    return items;
  }, [transcribeData]);

  // Auto-scroll to current time when following (works with Virtuoso)
  useEffect(() => {
    // Skip auto-scroll if user just clicked (let the click handler effect handle it)
    if (!isFollowing || !virtuosoRef.current || userClickedSegmentRef.current) return;

    // Get the index of the active item
    let activeIndex = -1;

    if (isWhisperFormat(transcription)) {
      activeIndex = whisperVirtualData.findIndex((item) => {
        if (item.type === "segment" && item.data) {
          return (
            throttledCurrentTime >= item.data.start - 0.1 &&
            throttledCurrentTime <= item.data.end + 0.1
          );
        }
        return false;
      });
    } else if (isTranscribeFormat(transcription) && transcribeData) {
      activeIndex = transcribeVirtualData.findIndex((item) => {
        if (item.type === "segment" && item.data) {
          return (
            throttledCurrentTime >= parseFloat(item.data.start_time) - 0.1 &&
            throttledCurrentTime <= parseFloat(item.data.end_time) + 0.1
          );
        }
        return false;
      });
    }

    if (activeIndex >= 0) {
      // Set flag to indicate auto-scrolling is happening
      isAutoScrollingRef.current = true;

      // Use scrollToIndex instead of scrollIntoView for better Virtuoso compatibility
      virtuosoRef.current.scrollToIndex({
        index: activeIndex,
        align: "center",
        behavior: "smooth",
        offset: 300,
      });

      // Reset the auto-scrolling flag after a longer delay to ensure all scroll events are handled
      setTimeout(() => {
        isAutoScrollingRef.current = false;
      }, 1500);
    }
  }, [
    throttledCurrentTime,
    isFollowing,
    whisperVirtualData,
    transcribeVirtualData,
    transcribeData,
    transcription,
  ]);

  // Handle user clicks on segments - scroll to clicked segment immediately
  useEffect(() => {
    if (userClickedSegmentRef.current && virtuosoRef.current) {
      // Find the segment that was clicked based on current audio time
      let clickedIndex = -1;

      if (isWhisperFormat(transcription)) {
        clickedIndex = whisperVirtualData.findIndex((item) => {
          if (item.type === "segment" && item.data) {
            return (
              throttledCurrentTime >= item.data.start - 0.1 &&
              throttledCurrentTime <= item.data.end + 0.1
            );
          }
          return false;
        });
      } else if (isTranscribeFormat(transcription) && transcribeData) {
        clickedIndex = transcribeVirtualData.findIndex((item) => {
          if (item.type === "segment" && item.data) {
            return (
              throttledCurrentTime >= parseFloat(item.data.start_time) - 0.1 &&
              throttledCurrentTime <= parseFloat(item.data.end_time) + 0.1
            );
          }
          return false;
        });
      }

      if (clickedIndex >= 0) {
        // Set flag to indicate auto-scrolling is happening to prevent conflicts
        isAutoScrollingRef.current = true;

        // Immediately scroll to the clicked segment
        virtuosoRef.current.scrollToIndex({
          index: clickedIndex,
          align: "center",
          behavior: "smooth",
          offset: 300,
        });

        // Reset the auto-scrolling flag after animation with longer delay
        setTimeout(() => {
          isAutoScrollingRef.current = false;
        }, 1500);
      }

      // Reset user clicked flag after a short delay to allow the scroll to happen
      // but not interfere with subsequent manual scrolling
      const timer = setTimeout(() => {
        userClickedSegmentRef.current = false;
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [
    throttledCurrentTime,
    whisperVirtualData,
    transcribeVirtualData,
    transcribeData,
    transcription,
  ]);

  if (!transcription) {
    return (
      <div className="flex items-center justify-center h-full">
        <p className="text-base-content/60">No transcription available</p>
      </div>
    );
  }

  // Determine which virtual data to use
  const isWhisper = isWhisperFormat(transcription);
  const virtualData = isWhisper ? whisperVirtualData : transcribeVirtualData;

  return (
    <div ref={containerRef} className="h-screen w-full flex flex-col">
      <dialog ref={confirmDialogRef} className="modal">
        <div className="modal-box prose w-11/12 md:w-5/12 p-8">
          <h3 className="text-lg font-semibold text-base-content mb-4">
            Confirm Narrative Regeneration
          </h3>
          <p className="text-gray-600 dark:text-base-content/70 mb-6">
            Are you sure you want to regenerate the narrative from this
            transcription? This will completely replace the current narrative
            content and you will lose all manual edits that have been made to
            the narrative.
          </p>
          <div className="modal-action">
            <Button
              variant="outline"
              onClick={() => confirmDialogRef.current?.close()}
              disabled={isRegenerating}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleRegenerateNarrative}
              loading={isRegenerating}
              disabled={isRegenerating}
            >
              Confirm
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => confirmDialogRef.current?.close()}>
            close
          </button>
        </form>
      </dialog>

      {/* Copy Transcription Modal (interaction audio) */}
      <dialog ref={copyDialogRef} className="modal">
        <div className="modal-box max-w-2xl">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-lg">Copy Transcription</h3>
            <button onClick={() => copyDialogRef.current?.close()}>
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Options */}
          <div className="flex items-center gap-6 mb-3">
            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600 dark:text-base-content/70">
              <input
                type="checkbox"
                className="checkbox checkbox-xs border-blue-500 [--chkbg:theme(colors.blue.500)] [--chkfg:theme(colors.blue.50)] checked:border-blue-500"
                checked={copyIncludeSpeakers}
                onChange={(e) => setCopyIncludeSpeakers(e.target.checked)}
              />
              Include speakers
            </label>
            <label className="flex items-center gap-2 cursor-pointer text-sm text-slate-600 dark:text-base-content/70">
              <input
                type="checkbox"
                className="checkbox checkbox-xs border-blue-500 [--chkbg:theme(colors.blue.500)] [--chkfg:theme(colors.blue.50)] checked:border-blue-500"
                checked={copyIncludeTimestamps}
                onChange={(e) => setCopyIncludeTimestamps(e.target.checked)}
              />
              Include timestamps
            </label>
          </div>

          {/* Segment list */}
          <div
            ref={copyListRef}
            className="max-h-[240px] overflow-y-auto rounded-lg border border-base-300 bg-base-100 mb-3"
          >
            <div className="px-3 py-2 border-b border-slate-100 dark:border-base-300 sticky top-0 bg-base-100 z-10 flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-slate-500 dark:text-base-content/60 m-0">
                Select the parts you want to copy:
              </p>
              <label className="flex items-center gap-1.5 cursor-pointer text-xs text-slate-500 dark:text-base-content/60 shrink-0">
                <input
                  type="checkbox"
                  className="checkbox checkbox-xs border-blue-500 [--chkbg:theme(colors.blue.500)] [--chkfg:theme(colors.blue.50)] checked:border-blue-500"
                  checked={
                    (transcribeData?.audioSegments.length ?? 0) > 0 &&
                    copySelectedSegmentIds.size ===
                      transcribeData?.audioSegments.length
                  }
                  onChange={(e) =>
                    setCopySelectedSegmentIds(
                      e.target.checked
                        ? new Set(
                            (transcribeData?.audioSegments ?? []).map(
                              (segment) => segment.id
                            )
                          )
                        : new Set()
                    )
                  }
                />
                Select all
              </label>
            </div>
            <div className="px-3 py-2 flex flex-col gap-0.5">
              {(transcribeData?.audioSegments ?? []).map((segment) => (
                <label
                  key={segment.id}
                  data-copy-segment-id={segment.id}
                  className="flex items-start gap-2 text-sm text-slate-600 dark:text-base-content/70 cursor-pointer hover:bg-blue-50 dark:hover:bg-blue-900/40 rounded p-1"
                >
                  <input
                    type="checkbox"
                    className="checkbox checkbox-xs mt-0.5 shrink-0 border-blue-500 [--chkbg:theme(colors.blue.500)] [--chkfg:theme(colors.blue.50)] checked:border-blue-500"
                    checked={copySelectedSegmentIds.has(segment.id)}
                    onChange={() => handleToggleCopySegment(segment.id)}
                  />
                  <span className="text-xs text-base-content/40 font-mono mt-0.5 shrink-0 w-9">
                    {formatTime(segment.start_time)}
                  </span>
                  <span>
                    <span className="font-semibold text-gray-700 dark:text-base-content/80">
                      {getSpeakerDisplayName(segment.speaker_label)}:{" "}
                    </span>
                    {segment.transcript}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Preview */}
          <div className="mb-4">
            <p className="text-xs font-medium text-slate-500 dark:text-base-content/60 m-0 mb-1">
              Preview
            </p>
            <div className="max-h-[160px] overflow-y-auto rounded-lg border border-base-300 bg-base-200 p-3 text-sm text-slate-600 dark:text-base-content/70 whitespace-pre-wrap">
              {copyPreviewText || (
                <span className="text-slate-400 dark:text-base-content/40">
                  Select at least one part to copy
                </span>
              )}
            </div>
          </div>

          <Button
            variant="primary"
            size="sm"
            className="w-full flex items-center justify-center gap-2"
            onClick={handleCopyFromDialog}
            disabled={!copyPreviewText}
          >
            <ClipboardDocumentIcon className="size-4" />
            Copy to Clipboard
          </Button>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={() => copyDialogRef.current?.close()}>close</button>
        </form>
      </dialog>

      {/* Speaker Name Edit Modal */}
      <dialog ref={speakerEditDialogRef} className="modal" data-tour="transcript-speaker-dialog">
        <div className="modal-box prose w-11/12 md:w-96 p-8">
          <h3 className="text-lg font-semibold text-base-content mb-4">
            Edit Speaker Name
          </h3>
          <div className="space-y-4">
            <div>
              <Input
                title="Speaker Name"
                type="text"
                value={editingSpeakerName}
                onChange={(e) => setEditingSpeakerName(e.target.value)}
                className="w-full"
                placeholder="Enter speaker name"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSaveSpeakerName(editingSpeakerNameId!);
                  } else if (e.key === "Escape") {
                    handleCancelSpeakerNameEdit();
                  }
                }}
              />
            </div>
            <div>
              <Input
                title="Initials (optional)"
                type="text"
                value={editingSpeakerInitials}
                onChange={(e) => setEditingSpeakerInitials(e.target.value)}
                className="w-full"
                placeholder="e.g., DB, CW"
                maxLength={2}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSaveSpeakerName(editingSpeakerNameId!);
                  } else if (e.key === "Escape") {
                    handleCancelSpeakerNameEdit();
                  }
                }}
              />
              <p className="text-xs text-base-content/60 mt-1">
                If left empty, initials will be auto-generated from the name
              </p>
            </div>
          </div>
          <div className="modal-action">
            <Button
              variant="outline"
              onClick={handleCancelSpeakerNameEdit}
              disabled={isSavingSpeakerName}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => handleSaveSpeakerName(editingSpeakerNameId!)}
              data-tour="transcript-speaker-save"
              disabled={isSavingSpeakerName || !editingSpeakerName.trim()}
              loading={isSavingSpeakerName}
            >
              Save
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={handleCancelSpeakerNameEdit}>close</button>
        </form>
      </dialog>

      {/* Add Speaker Dialog */}
      <dialog ref={addSpeakerDialogRef} className="modal">
        <div className="modal-box prose w-11/12 md:w-96 p-8">
          <h3 className="text-lg font-semibold text-base-content mb-4">
            Add New Speaker
          </h3>
          <div className="space-y-4">
            <div>
              <Input
                title="Speaker Name"
                type="text"
                value={newSpeakerName}
                onChange={(e) => setNewSpeakerName(e.target.value)}
                className="w-full"
                placeholder="Enter speaker name"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSaveNewSpeaker();
                  } else if (e.key === "Escape") {
                    handleCancelNewSpeaker();
                  }
                }}
              />
            </div>
            <div>
              <Input
                title="Initials (optional)"
                type="text"
                value={newSpeakerInitials}
                onChange={(e) => setNewSpeakerInitials(e.target.value)}
                className="w-full"
                placeholder="e.g., DB, CW"
                maxLength={2}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSaveNewSpeaker();
                  } else if (e.key === "Escape") {
                    handleCancelNewSpeaker();
                  }
                }}
              />
              <p className="text-xs text-base-content/60 mt-1">
                If left empty, initials will be auto-generated from the name
              </p>
            </div>
          </div>
          <div className="modal-action">
            <Button
              variant="outline"
              onClick={handleCancelNewSpeaker}
              disabled={isSavingNewSpeaker}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveNewSpeaker}
              disabled={isSavingNewSpeaker || !newSpeakerName.trim()}
              loading={isSavingNewSpeaker}
            >
              Add Speaker
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={handleCancelNewSpeaker}>close</button>
        </form>
      </dialog>

      {/* Delete Speaker Dialog */}
      <dialog ref={deleteSpeakerDialogRef} className="modal">
        <div className="modal-box prose w-11/12 md:w-5/12 p-8">
          <h3 className="text-lg font-semibold text-base-content mb-4">
            Confirm Speaker Deletion
          </h3>
          <p className="text-gray-600 dark:text-base-content/70 mb-4">
            Are you sure you want to delete this speaker? All segments assigned
            to this speaker will be updated to use the selected replacement
            speaker.
          </p>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 dark:text-base-content/80 mb-1">
              Replace with speaker:
            </label>
            <div className="relative">
              <select
                value={replacementSpeakerLabel}
                onChange={(e) => setReplacementSpeakerLabel(e.target.value)}
                className="select select-bordered w-full pr-10"
              >
                <option value="">Select a speaker</option>
                {Object.keys(effectiveSpeakerMap || {})
                  .filter(
                    (speakerLabel) => speakerLabel !== deletingSpeakerLabel
                  )
                  .map((speakerLabel) => {
                    // Use effectiveSpeakerMap which prioritizes case_files metadata
                    const speakerName = effectiveSpeakerMap?.[speakerLabel]?.name;
                    const speakerNumber = speakerLabel.replace(/\D/g, "");
                    const displayNumber = speakerNumber
                      ? parseInt(speakerNumber) + 1
                      : 1;
                    const displayName =
                      speakerName || `Speaker ${displayNumber}`;

                    return (
                      <option key={speakerLabel} value={speakerLabel}>
                        {displayName}
                      </option>
                    );
                  })}
              </select>
              <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                <ChevronDownIcon className="w-4 h-4 text-base-content/40" />
              </div>
            </div>
          </div>
          <div className="modal-action">
            <Button
              variant="outline"
              onClick={handleCancelDeleteSpeaker}
              disabled={isDeletingSpeaker}
            >
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={handleDeleteSpeaker}
              disabled={isDeletingSpeaker}
              loading={isDeletingSpeaker}
            >
              Delete Speaker
            </Button>
          </div>
        </div>
        <form method="dialog" className="modal-backdrop">
          <button onClick={handleCancelDeleteSpeaker}>close</button>
        </form>
      </dialog>

      {findPortal &&
        createPortal(
          <FindBar
            open={showFind}
            inputRef={findInputRef}
            searchTerm={findTerm}
            onSearch={handleFindSearch}
            current={findMatches.length ? findIndex + 1 : 0}
            total={findMatches.length}
            onNext={handleFindNext}
            onPrevious={handleFindPrevious}
            caseSensitive={findCaseSensitive}
            onToggleCaseSensitive={handleToggleFindCaseSensitive}
            onClose={handleCloseFind}
          />,
          findPortal
        )}

      <div className="flex-1 min-h-0 w-full">
        <Virtuoso
          ref={virtuosoRef}
          data={virtualData}
          totalCount={virtualData.length}
          itemContent={itemContent}
          className="h-full w-full"
          style={{ height: "100%", width: "100%" }}
          components={virtuosoComponents}
          overscan={5}
          increaseViewportBy={200}
          onScroll={handleVirtuosoScroll}
        />
      </div>
    </div>
  );
});

export default Transcription;
